import { useState } from "react";
import { usePublicPlans, useUsageBalance } from "@/hooks/use-usage";
import { useToast } from "@/hooks/use-toast";
import { TopUpModal } from "@/components/topup-modal";

// Rough credits cost of one finished, on-brand ad (campaign-v2 ≈ 407 credits
// per ~5-ad run, per action_cost_estimates). Used only to show an
// "≈ N ads/month" estimate on plan cards — tune here if pricing changes.
const CREDITS_PER_AD = 80;

export function UpgradeModal({ onClose }: { onClose: () => void }) {
  const { data, isLoading, error } = usePublicPlans();
  const { data: balanceData } = useUsageBalance();
  const currentPlanId = (balanceData?.plan as { id?: number } | null | undefined)?.id ?? null;
  const { toast } = useToast();
  const [checkingOutId, setCheckingOutId] = useState<number | null>(null);
  const [showTopup, setShowTopup] = useState(false);

  // Start a real Stripe Checkout for the chosen plan — same endpoint the
  // sign-up plan picker uses. The webhook + /?stripe=success return handler
  // (already wired for sign-up) assigns the plan once payment lands.
  const startCheckout = async (planId: number) => {
    setCheckingOutId(planId);
    try {
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast({
          title: "Couldn’t start checkout",
          description: body?.message || "Please try again, or contact support.",
          variant: "destructive",
        });
        setCheckingOutId(null);
        return;
      }
      const { url } = (await res.json()) as { url?: string };
      if (url) {
        window.location.href = url; // → Stripe Checkout
      } else {
        toast({ title: "Checkout unavailable", description: "No checkout URL was returned.", variant: "destructive" });
        setCheckingOutId(null);
      }
    } catch {
      toast({ title: "Couldn’t start checkout", description: "Network error — please try again.", variant: "destructive" });
      setCheckingOutId(null);
    }
  };

  const hasStripeSub = !!balanceData?.hasStripeSubscription;
  const [openingPortal, setOpeningPortal] = useState(false);

  // Existing Stripe subscribers change plans in the Customer Portal (one sub,
  // Stripe handles proration + downgrade timing) — never a second checkout.
  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await fetch("/api/billing/portal", { method: "POST", credentials: "include" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast({ title: "Couldn’t open billing portal", description: body?.message || "Please try again.", variant: "destructive" });
        setOpeningPortal(false);
        return;
      }
      const { url } = (await res.json()) as { url?: string };
      if (url) window.location.href = url; // → Stripe Customer Portal
      else { toast({ title: "Portal unavailable", variant: "destructive" }); setOpeningPortal(false); }
    } catch {
      toast({ title: "Couldn’t open billing portal", description: "Network error — please try again.", variant: "destructive" });
      setOpeningPortal(false);
    }
  };
  // Sort by price ascending, but push priceCents=0 ("Custom" / Enterprise)
  // plans to the END. Otherwise Enterprise lands first in the grid because
  // its 0 sorts below every paid plan, which reads as "free trial" and
  // leaves a paid plan dangling alone in the second row.
  const plans = (data?.rows ?? [])
    .filter((p: any) => !p.isArchived)
    .sort((a: any, b: any) => {
      const aCustom = a.priceCents === 0;
      const bCustom = b.priceCents === 0;
      if (aCustom && !bCustom) return 1;
      if (!aCustom && bCustom) return -1;
      return a.priceCents - b.priceCents;
    });

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="upgrade-modal"
    >
      <div
        className="max-h-[85vh] w-full max-w-4xl overflow-auto rounded bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Upgrade plan</h2>
            <p className="mt-1 text-xs text-neutral-500">
              Switch or upgrade anytime — secure checkout via Stripe.
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Top-ups are for existing customers only — only show once the user
                has an active plan (never on first-time signup). */}
            {balanceData?.plan && (
              <button
                type="button"
                onClick={() => setShowTopup(true)}
                className="text-xs font-medium text-neutral-700 underline-offset-2 hover:underline"
                data-testid="open-topup"
              >
                Top Up Account
              </button>
            )}
            <button onClick={onClose} className="text-neutral-500 hover:text-neutral-800" data-testid="upgrade-modal-close">✕</button>
          </div>
        </div>

        {isLoading && <div className="p-8 text-sm text-neutral-500">Loading plans…</div>}
        {error && <div className="p-8 text-sm text-red-600">Failed to load plans.</div>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan: any) => {
            const priceLabel = plan.priceCents === 0 ? "Custom" : `$${(plan.priceCents / 100).toFixed(0)}`;
            // Filter out the literal "custom" feature bullet — it's
            // redundant on the Enterprise card whose price already reads
            // "Custom", and it's never a meaningful feature to highlight.
            const featureList: string[] = (Array.isArray(plan.features) ? plan.features : [])
              .filter((f: unknown) => typeof f === "string" && f.toLowerCase().trim() !== "custom");
            const isCurrent = currentPlanId != null && plan.id === currentPlanId;
            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded border p-4 ${isCurrent ? "border-black bg-neutral-50" : "border-neutral-200"}`}
                data-testid={`upgrade-plan-${plan.id}`}
              >
                {isCurrent && (
                  <span
                    className="absolute right-3 top-3 rounded-full bg-black px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white"
                    data-testid={`upgrade-plan-${plan.id}-current-badge`}
                  >
                    Current
                  </span>
                )}
                <h3 className="text-base font-semibold">{plan.name}</h3>
                <div className="my-2 text-2xl font-bold">
                  {priceLabel}
                  {plan.priceCents > 0 && (
                    <span className="text-sm font-normal text-neutral-500">/mo</span>
                  )}
                </div>
                <div className="text-sm text-neutral-600">
                  {plan.priceCents === 0
                    ? "Contact us for details"
                    : `${Number(plan.creditsPerMonth).toLocaleString()} credits / month`}
                </div>
                {plan.priceCents > 0 && (
                  <div className="mt-0.5 text-sm font-medium text-neutral-900">
                    ≈ {(Math.floor(Number(plan.creditsPerMonth) / CREDITS_PER_AD / 10) * 10).toLocaleString()} ads / month
                  </div>
                )}
                <ul className="mt-3 flex-1 space-y-1 text-sm text-neutral-700">
                  {featureList.map((f) => (
                    <li key={f}>• {f.replaceAll("_", " ")}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button
                    type="button"
                    disabled
                    className="mt-4 block w-full cursor-not-allowed rounded bg-neutral-200 px-3 py-2 text-center text-sm text-neutral-500"
                    data-testid={`upgrade-plan-${plan.id}-current-cta`}
                  >
                    Your current plan
                  </button>
                ) : plan.priceCents === 0 ? (
                  <a
                    href={`mailto:support@admoji.com?subject=${encodeURIComponent(`Upgrade to ${plan.name}`)}`}
                    className="mt-4 block rounded bg-black px-3 py-2 text-center text-sm text-white hover:bg-neutral-800"
                    data-testid={`upgrade-plan-${plan.id}-contact`}
                  >
                    Contact Us
                  </a>
                ) : hasStripeSub ? (
                  <button
                    type="button"
                    onClick={openPortal}
                    disabled={openingPortal}
                    className="mt-4 block w-full rounded bg-black px-3 py-2 text-center text-sm text-white hover:bg-neutral-800 disabled:opacity-60"
                    data-testid={`upgrade-plan-${plan.id}-manage`}
                  >
                    {openingPortal ? "Opening…" : "Manage plan"}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => startCheckout(plan.id)}
                    disabled={checkingOutId !== null}
                    className="mt-4 block w-full rounded bg-black px-3 py-2 text-center text-sm text-white hover:bg-neutral-800 disabled:opacity-60"
                    data-testid={`upgrade-plan-${plan.id}-checkout`}
                  >
                    {checkingOutId === plan.id ? "Starting checkout…" : `Get ${plan.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
    {showTopup && <TopUpModal onClose={() => setShowTopup(false)} />}
    </>
  );
}
